import { useEffect, useRef, useState } from "react";
import { useDebounce } from "usehooks-ts";

import { getCurrentLocale, getCurrentRegion } from "../../../utils";

// AU requires explicit street and unit numbers because the Google Places API
// won't prefill unit/apartment details when a prediction is selected.
const strictAddressRegions = ["AU"];

interface Options {
  region?: string;
  locale?: string;
  input: string;
}

interface UseAddressPredictionsResult {
  predictions: google.maps.places.AutocompletePrediction[];
  getDetails(placeId: string): Promise<google.maps.places.PlaceResult | null>;
  isStrictRegion?: boolean;
}

export function useAddressPredictions({
  input,
  region,
  locale,
}: Options): UseAddressPredictionsResult {
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const debouncedValue = useDebounce<string>(input, 200);
  const autocomplete = useRef<google.maps.places.AutocompleteService | null>(
    null
  );

  const resolvedRegion = region ?? getCurrentRegion();
  const isStrictRegion = strictAddressRegions.includes(
    resolvedRegion.toUpperCase()
  );

  // initialize Autocomplete once window.google detetcted
  useEffect(() => {
    if (!autocomplete.current && window?.google) {
      try {
        autocomplete.current =
          new window.google.maps.places.AutocompleteService();
      } catch (e) {
        console.error("Cannot connect to Google Places API", e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.google]);

  useEffect(() => {
    if (!debouncedValue) {
      setPredictions([]);
      return;
    }

    const request: google.maps.places.AutocompletionRequest = {
      input: debouncedValue,
      region: resolvedRegion,
      language: locale ?? getCurrentLocale(),
    };

    try {
      autocomplete.current?.getPlacePredictions(request).then((response) => {
        setPredictions(response.predictions);
      });
    } catch (e) {
      console.error("Cannot retrieve Google Predictions", e);
    }
  }, [debouncedValue, locale, resolvedRegion, isStrictRegion, setPredictions]);

  const getDetails = async function (
    placeId: string
  ): Promise<google.maps.places.PlaceResult | null> {
    if (!autocomplete.current) return null;

    // we're not using a map, this element is required to fetch place details
    const map = new google.maps.Map(document.createElement("div"));
    const googlePlaces = new google.maps.places.PlacesService(map);

    return new Promise(function (resolve, _) {
      googlePlaces.getDetails({ placeId }, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK)
          resolve(place);
      });
    });
  };

  return {
    predictions,
    getDetails,
    isStrictRegion,
  };
}
